// pages/LeaveNew/addleave.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './add-leave.css';
import { RiAddCircleFill } from 'react-icons/ri';
import { FaCalendarDay, FaCalendarWeek, FaInfoCircle, FaPaperPlane, FaExclamationTriangle, FaUserFriends } from 'react-icons/fa';
import { TbMoodCry, TbAlertCircle, TbDropletFilled } from 'react-icons/tb';

const AddLeave = () => {
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    allowanceType: '',
    reason: ''
  });
  const [availableTypes, setAvailableTypes] = useState([]);
  const [userData, setUserData] = useState({
    userId: '',
    name: '',
    username: '',
    department: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [checkingBalance, setCheckingBalance] = useState(false);
  const [noLeavesLeft, setNoLeavesLeft] = useState(false);
  const [showDepartmentConflictModal, setShowDepartmentConflictModal] = useState(false);
  const [conflictMessage, setConflictMessage] = useState('');
  const [conflictCount, setConflictCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const name = localStorage.getItem('name');
    const username = localStorage.getItem('username');
    const department = localStorage.getItem('department');

    if (!userId || !username || !department) {
      toast.error('Session expired. Please log in again.');
      navigate('/login');
      return;
    }

    setUserData({
      userId,
      name: name || '',
      username,
      department
    });
  }, [navigate]);

  const fetchAvailableLeaveTypes = async () => {
    if (!userData.userId) return false;
    try {
      const { data } = await axios.get(
        `https://backend.ems.hiradentallab.com/api/leavesmanual/types/${userData.userId}`
      );
      if (data.success) {
        const types = data.data.leaveTypes || [];
        setAvailableTypes(types);
        const hasLeaves = types.length > 0;
        setNoLeavesLeft(!hasLeaves);
        return hasLeaves;
      } else {
        setNoLeavesLeft(true);
        return false;
      }
    } catch (error) {
      console.error('Error fetching leave types:', error);
      setNoLeavesLeft(true);
      return false;
    }
  };

  useEffect(() => {
    if (userData.userId) {
      setLoading(true);
      fetchAvailableLeaveTypes().finally(() => setLoading(false));
    }
  }, [userData.userId]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const err = {};
    if (!formData.startDate) err.startDate = 'Start date is required';
    if (!formData.endDate) err.endDate = 'End date is required';
    if (!formData.allowanceType) err.allowanceType = 'Please select allowance type';
    if (!formData.reason.trim()) err.reason = 'Reason is required';
    
    // Validate dates
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end < start) {
        err.endDate = 'End date cannot be before start date';
      }
    }
    
    return err;
  };

  const showDepartmentConflictError = (data) => {
    let message = 'Oh no! Someone from your department has already applied for leave during this period.';
    
    // Check if backend provided a custom message
    if (data.message) {
      message = data.message;
    }
    
    setConflictMessage(message);
    
    // Get conflict count
    if (data.data && data.data.conflictingCount) {
      setConflictCount(data.data.conflictingCount);
    } else if (data.data && data.data.conflictingLeaves) {
      setConflictCount(data.data.conflictingLeaves.length);
    } else {
      setConflictCount(1);
    }
    
    setShowDepartmentConflictModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    
    // Check balance first
    setCheckingBalance(true);
    const hasAvailableLeaves = await fetchAvailableLeaveTypes();
    
    if (!hasAvailableLeaves) {
      toast.error('You have no remaining leaves. You cannot apply for a new leave at this time.', {
        position: 'top-center',
        autoClose: false
      });
      setCheckingBalance(false);
      return;
    }
    
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setCheckingBalance(false);
      return;
    }

    if (!userData.name) {
      toast.error('User name is missing. Please contact administrator.');
      setCheckingBalance(false);
      return;
    }

    const startDateUTC = new Date(formData.startDate + 'T00:00:00.000Z');
    const endDateUTC = new Date(formData.endDate + 'T00:00:00.000Z');
    
    const payload = {
      userId: parseInt(userData.userId),
      name: userData.name.trim(),
      username: userData.username.trim(),
      department: userData.department.trim(),
      startDate: startDateUTC.toISOString(),
      endDate: endDateUTC.toISOString(),
      allowanceType: formData.allowanceType,
      reason: formData.reason.trim(),
    };

    setLoading(true);
    try {
      const { data } = await axios.post(
        'https://backend.ems.hiradentallab.com/api/leavesmanual/apply',
        payload,
        { headers: { 'Content-Type': 'application/json' }, timeout: 15000 }
      );
      
      if (data.success) {
        toast.success(`Leave applied successfully for ${data.data.daysCount || 'several'} working day(s)!`, {
          position: 'top-center',
          autoClose: 4000
        });
        setTimeout(() => navigate('/my-leaves-status'), 1500);
      } else {
        // Check if it's a department conflict error
        if (data.errorCode === 'DEPARTMENT_CONFLICT' || 
            data.message?.toLowerCase().includes('department') || 
            data.message?.toLowerCase().includes('already applied')) {
          showDepartmentConflictError(data);
        } else {
          toast.error(data.message || 'Failed to apply leave.');
        }
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to apply leave. Please try again later.';
      
      // Check for department conflict in error response
      if (error.response?.data?.errorCode === 'DEPARTMENT_CONFLICT' || 
          msg.toLowerCase().includes('department') || 
          msg.toLowerCase().includes('already applied')) {
        showDepartmentConflictError(error.response?.data || { message: msg });
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
      setCheckingBalance(false);
    }
  };

  const typeLabels = {
    yearly: 'Yearly Holiday Allowance',
    december: 'December Holiday Allowance',
    simple: 'Sick / Emergency Leave',
  };

  const closeModal = () => {
    setShowDepartmentConflictModal(false);
    setConflictCount(0);
  };

  return (
    <div className="add-leave-page">
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Department Conflict Modal */}
      {showDepartmentConflictModal && (
        <div className="add-department-conflict-modal">
          <div className="add-modal-overlay" onClick={closeModal}></div>
          <div className="add-modal-content">
            <div className="add-modal-header">
              <div className="add-modal-icon-container">
                <div className="add-3d-emoji-container">
                  <div className="add-3d-emoji">
                    <TbMoodCry size={80} />
                  </div>
                  
                  {/* Left side tears */}
                  <div className="add-tear-drop add-left-tear-1">
                    <TbDropletFilled size={12} />
                  </div>
                  <div className="add-tear-drop add-left-tear-2">
                    <TbDropletFilled size={10} />
                  </div>
                  <div className="add-tear-drop add-left-tear-3">
                    <TbDropletFilled size={8} />
                  </div>
                  
                  {/* Right side tears */}
                  <div className="add-tear-drop add-right-tear-1">
                    <TbDropletFilled size={12} />
                  </div>
                  <div className="add-tear-drop add-right-tear-2">
                    <TbDropletFilled size={10} />
                  </div>
                  <div className="add-tear-drop add-right-tear-3">
                    <TbDropletFilled size={8} />
                  </div>
                </div>
                
                <div className="add-alert-emoji add-alert-animation">
                  <TbAlertCircle size={40} />
                </div>
              </div>
              <h3 className="add-modal-title">Leave Conflict Detected!</h3>
              <p className="add-modal-subtitle">
                <FaUserFriends className="add-me-1" />
                {conflictCount} colleague{conflictCount > 1 ? 's' : ''} from your department has overlapping leave
              </p>
            </div>
            
            <div className="add-modal-body">
              <div className="add-conflict-message">
                <FaExclamationTriangle className="add-warning-icon" />
                <p>{conflictMessage}</p>
              </div>
              
              <div className="add-conflict-info-box">
                <h5>Why this happens?</h5>
                <p>To maintain smooth operations, only limited team members from the same department can be on leave at the same time.</p>
              </div>
              
              <div className="add-suggestion-box">
                <h5>What to do?</h5>
                <ul>
                  <li>Choose different dates for your leave</li>
                  <li>Check with your team about their leave schedule</li>
                </ul>
              </div>
            </div>
            
            <div className="add-modal-footer">
              <button 
                className="add-modal-close-btn" 
                onClick={closeModal}
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="add-leave-wrapper">
        {(loading || checkingBalance) && (
          <div className="add-leave-loading">
            <Spinner animation="border" variant="primary" />
            <p className="add-loading-text">
              {checkingBalance ? 'Checking your leave balance...' : 'Processing...'}
            </p>
          </div>
        )}

        {!loading && !checkingBalance && noLeavesLeft && (
          <div className="add-no-leaves-card">
            <div className="add-no-leaves-icon">
              <FaCalendarWeek />
            </div>
            <h4>No Leaves Available</h4>
            <p>You have used all your allotted leaves.</p>
            <p className="add-small-text">Contact HR if you believe this is an error.</p>
          </div>
        )}

        {!loading && !checkingBalance && !noLeavesLeft && availableTypes.length > 0 && (
          <div className="add-leave-form-card">
            <div className="add-card-header">
              <h3>Apply for Leave</h3>
              <p>Fill in the details below to submit your leave request</p>
              <small style={{ color: '#666', fontStyle: 'italic' }}>
                You can now apply for backdated leaves (previous dates).
              </small>
            </div>

            <form onSubmit={handleSubmit} className="add-leave-form">
              <div className="add-form-grid">
                <div className="add-form-group">
                  <label className="add-form-label">
                    <FaCalendarDay className="add-label-icon" />
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    className={`add-form-control ${errors.startDate ? 'add-error' : ''}`}
                    // Removed min attribute → allows past dates
                    disabled={loading || checkingBalance}
                  />
                  {errors.startDate && <span className="add-error-message">{errors.startDate}</span>}
                </div>

                <div className="add-form-group">
                  <label className="add-form-label">
                    <FaCalendarDay className="add-label-icon" />
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                    className={`add-form-control ${errors.endDate ? 'add-error' : ''}`}
                    // Only enforce end >= start, no future-only restriction
                    min={formData.startDate || undefined}
                    disabled={loading || checkingBalance || !formData.startDate}
                  />
                  {errors.endDate && <span className="add-error-message">{errors.endDate}</span>}
                </div>

                <div className="add-form-group add-full-width">
                  <label className="add-form-label">
                    <FaInfoCircle className="add-label-icon" />
                    Allowance Type
                  </label>
                  <select
                    value={formData.allowanceType}
                    onChange={(e) => handleInputChange('allowanceType', e.target.value)}
                    className={`add-form-control ${errors.allowanceType ? 'add-error' : ''}`}
                    disabled={loading || checkingBalance}
                  >
                    <option value="">-- Select Type --</option>
                    {availableTypes.map((type) => (
                      <option key={type} value={type}>
                        {typeLabels[type] || type}
                      </option>
                    ))}
                  </select>
                  {errors.allowanceType && <span className="add-error-message">{errors.allowanceType}</span>}
                </div>

                <div className="add-form-group add-full-width">
                  <label className="add-form-label">
                    <FaInfoCircle className="add-label-icon" />
                    Reason for Leave
                  </label>
                  <textarea
                    value={formData.reason}
                    onChange={(e) => handleInputChange('reason', e.target.value)}
                    className={`add-form-control ${errors.reason ? 'add-error' : ''}`}
                    rows="4"
                    disabled={loading || checkingBalance}
                    placeholder="Please provide a detailed reason for your leave application..."
                  />
                  {errors.reason && <span className="add-error-message">{errors.reason}</span>}
                </div>
              </div>

              {errors.api && (
                <Alert variant="danger" className="add-form-alert">
                  {errors.api}
                </Alert>
              )}

              <div className="add-form-actions">
                <button
                  type="submit"
                  disabled={loading || checkingBalance}
                  className="add-submit-button"
                >
                  {(loading || checkingBalance) ? (
                    <>
                      <Spinner animation="border" size="sm" className="add-me-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane className="add-me-2" />
                      Submit Leave Request
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddLeave;